import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbortError } from '@/services/composeService';
import { DICTATION_TEXT, TRANSCRIBE_ERROR_TEXT, errorText } from './messages';

/** Auto-stop: longer dictations are unusual in a chat and cost more to transcribe. */
export const MAX_DICTATION_MS = 120_000;
/** Shorter than this is a mis-click, not speech. */
export const MIN_DICTATION_MS = 700;
const TICK_MS = 250;
/** Speech RMS sits around 0.03–0.25; this maps it onto a readable 0–1 meter. */
const LEVEL_GAIN = 4;

/** Best first: opus in webm (Chrome/Firefox/Edge), ogg, then mp4/AAC (Safari). */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

type RecorderClass = typeof MediaRecorder;

function recorderClass(): RecorderClass | undefined {
  return typeof window === 'undefined' ? undefined : (window as { MediaRecorder?: RecorderClass }).MediaRecorder;
}

function browserSupports(type: string): boolean {
  const Recorder = recorderClass();
  return !!Recorder && typeof Recorder.isTypeSupported === 'function' && Recorder.isTypeSupported(type);
}

/** The best container the browser can record; undefined = let the browser choose. */
export function pickRecorderMimeType(isSupported: (type: string) => boolean = browserSupports): string | undefined {
  return MIME_CANDIDATES.find((type) => {
    try {
      return isSupported(type);
    } catch {
      return false;
    }
  });
}

export function isDictationSupported(): boolean {
  return typeof recorderClass() === 'function' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

function mediaErrorText(error: unknown): string {
  const name = (error as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return DICTATION_TEXT.denied;
    case 'NotFoundError':
    case 'OverconstrainedError':
    case 'DevicesNotFoundError':
      return DICTATION_TEXT.noMicrophone;
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return DICTATION_TEXT.busy;
    default:
      return DICTATION_TEXT.startFailed;
  }
}

export type DictationStatus = 'idle' | 'requesting' | 'recording' | 'transcribing';

type Options = {
  /** Uploads the recording; resolves with the recognised text ("" = nothing understood). */
  transcribe: (audio: Blob, signal: AbortSignal) => Promise<string>;
  /** Receives the recognised text. */
  onText: (text: string) => void;
  maxDurationMs?: number;
  minDurationMs?: number;
};

type AudioProbe = { context: AudioContext; analyser: AnalyserNode; buffer: Uint8Array };

/**
 * Push-to-talk dictation: MediaRecorder with the best supported format, an
 * input level from an AnalyserNode, auto-stop after two minutes, cancel at
 * any point. The microphone is released the moment recording ends — on stop,
 * cancel, error and unmount alike.
 */
export function useDictation({
  transcribe,
  onText,
  maxDurationMs = MAX_DICTATION_MS,
  minDurationMs = MIN_DICTATION_MS,
}: Options) {
  const [status, setStatusState] = useState<DictationStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const statusRef = useRef<DictationStatus>('idle');
  /** Bumped by cancel/unmount: every async continuation of an older session gives up. */
  const sessionRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<AudioProbe | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef(0);
  const uploadRef = useRef<AbortController | null>(null);
  const transcribeRef = useRef(transcribe);
  const onTextRef = useRef(onText);
  transcribeRef.current = transcribe;
  onTextRef.current = onText;

  const setStatus = useCallback((next: DictationStatus) => {
    statusRef.current = next;
    setStatusState(next);
  }, []);

  const release = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    tickRef.current = null;
    autoStopRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    const audio = audioRef.current;
    audioRef.current = null;
    audio?.context.close().catch(() => undefined);
  }, []);

  const attachLevelProbe = useCallback((stream: MediaStream) => {
    const Context =
      window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) return;
    try {
      const context = new Context();
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      context.createMediaStreamSource(stream).connect(analyser);
      // Safari starts a context created after an await suspended.
      context.resume().catch(() => undefined);
      audioRef.current = { context, analyser, buffer: new Uint8Array(analyser.fftSize) };
    } catch {
      audioRef.current = null; // the meter is a nicety; recording works without it
    }
  }, []);

  /** Current input level 0–1 (0 when no meter is available). Cheap; call per frame. */
  const getLevel = useCallback((): number => {
    const audio = audioRef.current;
    if (!audio) return 0;
    audio.analyser.getByteTimeDomainData(audio.buffer);
    let sum = 0;
    for (let i = 0; i < audio.buffer.length; i += 1) {
      const sample = (audio.buffer[i] - 128) / 128;
      sum += sample * sample;
    }
    return Math.min(1, Math.sqrt(sum / audio.buffer.length) * LEVEL_GAIN);
  }, []);

  const finish = useCallback(
    async (session: number, chunks: Blob[], mimeType: string) => {
      // A stale recorder (cancelled, then a new recording started) must not
      // touch the refs: they belong to the new session's microphone now.
      // Whoever ended the old session already released its stream.
      if (session !== sessionRef.current) return;
      const duration = Date.now() - startedAtRef.current;
      release();
      setElapsedMs(duration);
      const audio = new Blob(chunks, { type: mimeType });
      if (duration < minDurationMs || audio.size === 0) {
        setStatus('idle');
        setError(DICTATION_TEXT.tooShort);
        return;
      }
      setStatus('transcribing');
      const controller = new AbortController();
      uploadRef.current = controller;
      try {
        const text = (await transcribeRef.current(audio, controller.signal)).trim();
        if (session !== sessionRef.current) return;
        setStatus('idle');
        if (!text) {
          setError(DICTATION_TEXT.nothingUnderstood);
          return;
        }
        onTextRef.current(text);
      } catch (err) {
        if (session !== sessionRef.current || controller.signal.aborted || isAbortError(err)) return;
        setStatus('idle');
        setError(errorText(err, TRANSCRIBE_ERROR_TEXT));
      } finally {
        if (uploadRef.current === controller) uploadRef.current = null;
      }
    },
    [minDurationMs, release, setStatus],
  );

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (statusRef.current !== 'recording' || !recorder) return;
    recorderRef.current = null;
    if (recorder.state !== 'inactive') recorder.stop(); // → onstop → finish
  }, []);

  const failStart = useCallback(
    (message: string) => {
      recorderRef.current = null;
      release();
      setStatus('idle');
      setError(message);
    },
    [release, setStatus],
  );

  const start = useCallback(async () => {
    if (statusRef.current !== 'idle') return;
    setError(null);
    if (!isDictationSupported()) {
      setError(DICTATION_TEXT.unsupported);
      return;
    }
    sessionRef.current += 1;
    const session = sessionRef.current;
    setStatus('requesting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
    } catch (err) {
      if (session !== sessionRef.current) return;
      setStatus('idle');
      setError(mediaErrorText(err));
      return;
    }
    if (session !== sessionRef.current) {
      // Cancelled (or unmounted) while the permission prompt was open.
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    streamRef.current = stream;

    const mimeType = pickRecorderMimeType();
    const Recorder = recorderClass() as RecorderClass;
    let recorder: MediaRecorder;
    try {
      recorder = new Recorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      failStart(DICTATION_TEXT.startFailed);
      return;
    }
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      void finish(session, chunks, recorder.mimeType || mimeType || 'audio/webm');
    };
    recorder.onerror = () => {
      if (session !== sessionRef.current) return;
      sessionRef.current += 1;
      failStart(DICTATION_TEXT.startFailed);
    };
    recorderRef.current = recorder;
    attachLevelProbe(stream);
    try {
      recorder.start();
    } catch {
      failStart(DICTATION_TEXT.startFailed);
      return;
    }
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setStatus('recording');
    tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), TICK_MS);
    autoStopRef.current = setTimeout(stop, maxDurationMs);
  }, [attachLevelProbe, failStart, finish, maxDurationMs, setStatus, stop]);

  /** Discard the recording or the running upload; nothing reaches the text field. */
  const cancel = useCallback(() => {
    sessionRef.current += 1;
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // already stopped by the browser
      }
    }
    uploadRef.current?.abort();
    uploadRef.current = null;
    release();
    setElapsedMs(0);
    setStatus('idle');
  }, [release, setStatus]);

  const dismissError = useCallback(() => setError(null), []);

  useEffect(
    () => () => {
      sessionRef.current += 1;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {
          // already stopped
        }
      }
      uploadRef.current?.abort();
      release();
    },
    [release],
  );

  return { status, elapsedMs, error, start, stop, cancel, getLevel, dismissError };
}
